<script setup lang="ts">
import { DropdownMenuItem, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from '@/components/dropdown-menu'
import type { MenuItemType } from './types'
import { useRouter } from 'vue-router'

const props = defineProps({
  item: {
    type: Object as () => MenuItemType,
    required: true
  }
})

const router = useRouter()

const handleAction = (item: MenuItemType) => {
  if (item.disabled) return
  if (item.action) {
    item.action()
  }
}
</script>

<template>
  <!-- External link item -->
  <DropdownMenuItem as-child
    v-if="item.href"
    :disabled="item.disabled"
    class="px-3"
  >
    <a
      v-if="!item.disabled"
      class="flex w-full items-center justify-between gap-2"
      :href="item.href"
      :target="item.target || '_self'"
      rel="noopener noreferrer"
      @click="handleAction(item)"
    >
      <span class="min-w-0">{{ item.label }}<span v-if="item.srLabel" class="sr-only">, {{ item.srLabel }}</span></span>
      <component
        v-if="item.icon"
        :is="item.icon"
        v-bind="item.iconProps"
      />
    </a>
    <span v-else class="flex w-full items-center">{{ item.label }}</span>
  </DropdownMenuItem>

  <!-- Router link item -->
  <DropdownMenuItem as-child
    v-else-if="item.isRouterLink && item.route" 
    @click="handleAction(item)"
    :disabled="item.disabled"
    class="px-3"
  >
    <router-link
      v-if="!item.disabled"
      class="flex w-full items-center"
      :to="item.route"
    >
      {{ item.label }}<span v-if="item.srLabel" class="sr-only">, {{ item.srLabel }}</span>
    </router-link>
    <span v-else class="flex w-full items-center">{{ item.label }}</span>
  </DropdownMenuItem>
  
  <!-- Submenu item with nested items -->
  <DropdownMenuSub v-else-if="item.submenuItems && item.submenuItems.length > 0">
    <DropdownMenuSubTrigger class="px-3 justify-between" :disabled="item.disabled">
      {{ item.label }}
      <template #icon>
        <span class="material-icons-round">arrow_right</span>
      </template>
    </DropdownMenuSubTrigger>
    <DropdownMenuSubContent class="w-40 px-0 py-2" data-testid="dropdown-menu-sub-content">
      <!-- Recursive component usage for each submenu item -->
      <template v-for="(subItem, subIndex) in item.submenuItems" :key="subIndex">
        <NestedMenuItem 
          v-if="subItem.submenuItems && subItem.submenuItems.length > 0"
          :item="subItem"
        />
        <DropdownMenuItem 
          v-else
          @click="handleAction(subItem)" 
          :disabled="subItem.disabled"
          class="px-3"
        >
          {{ subItem.label }}
        </DropdownMenuItem>
      </template>
    </DropdownMenuSubContent>
  </DropdownMenuSub>
  
  <!-- Regular menu item -->
  <DropdownMenuItem 
    v-else
    @click="handleAction(item)"
    :disabled="item.disabled"
    class="px-3"
  >
    <span class="flex w-full items-center justify-between gap-2">
      <span class="min-w-0">{{ item.label }}<span v-if="item.srLabel" class="sr-only">, {{ item.srLabel }}</span></span>
      <component
        v-if="item.icon"
        :is="item.icon"
        v-bind="item.iconProps"
      />
    </span>
  </DropdownMenuItem>
</template>
